/**
 * Écran des **améliorations permanentes** `/ameliorations` (DECISIONS §6.9, F18).
 *
 * Ce que l’écran affiche vient du serveur, sans exception :
 * - le **solde** est celui de `GET /me/upgrades` (et, après un achat, celui de la réponse
 *   d’achat) — il n’est jamais décrémenté localement (DECISIONS §8) ;
 * - le **niveau** possédé et le **coût du prochain niveau** (`coutProchain`, `null` au maximum)
 *   viennent de la même réponse ;
 * - les libellés (nom, description, effet, niveau maximum) viennent du **catalogue de contenu**
 *   publié par `GET /content/manifest` : aucune valeur n’est recopiée dans le client.
 *
 * Achat : `POST /me/upgrades/:upgradeId` avec un corps **vide**. Le client envoie seulement
 * l’identifiant de l’entrée du catalogue et une `Idempotency-Key` **stable par intention**
 * (DECISIONS §4.1) ; un double-clic ne produit qu’une requête (verrou synchrone + bouton
 * désactivé). Le serveur peut refuser (`INSUFFICIENT_ECLATS`, `UPGRADE_MAX_LEVEL`,
 * `RUN_INCOHERENT`) : le refus est affiché en clair et rien n’est modifié localement.
 */
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import {
  acheterAmelioration,
  chargerManifeste,
  cleI18nErreur,
  genererCleIdempotence,
  lireAmeliorations,
  useSessionStore,
  versErreurApi,
  type ErreurApi,
} from '../api';
import { cleTraductionContenu, formaterNombre } from '../i18n';
import { BandeauErreur } from '../ui/BandeauErreur';
import { Bouton } from '../ui/Bouton';
import { Chargement } from '../ui/Chargement';
import { Entete } from '../ui/Entete';

import type { AmeliorationJoueur } from '@elemora/shared/api';
import type { AmeliorationPermanente, EffetAmeliorationPermanente } from '@elemora/shared/content';

/** Données d’un chargement réussi : solde serveur, état du joueur, catalogue publié. */
interface DonneesAmeliorations {
  readonly eclats: number;
  readonly upgrades: readonly AmeliorationJoueur[];
  readonly catalogue: readonly AmeliorationPermanente[];
}

/** Confirmation d’achat, construite uniquement à partir de la réponse du serveur. */
interface ConfirmationAchat {
  readonly nom: string;
  readonly niveau: number;
  readonly solde: number;
}

/** Valeur du gain par niveau d’une amélioration (données publiques du catalogue). */
function valeurEffet(effet: EffetAmeliorationPermanente): number {
  return effet.valeur;
}

/**
 * Une ligne de la liste : le niveau possédé vient du serveur, les libellés du catalogue.
 *
 * Aucun repli inventé : si le catalogue publié ne contenait plus une entrée que le serveur
 * renvoie encore, le nom affiché serait l’identifiant brut — visiblement incomplet plutôt que
 * joliment faux.
 */
interface EntreeAmelioration {
  readonly etat: AmeliorationJoueur;
  readonly definition: AmeliorationPermanente | null;
}

export function EcranAmeliorations() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const progression = useSessionStore((etat) => etat.progression);
  const definirProgression = useSessionStore((etat) => etat.definirProgression);

  const [donnees, setDonnees] = useState<DonneesAmeliorations | null>(null);
  const [erreurChargement, setErreurChargement] = useState<ErreurApi | null>(null);
  const [erreurAchat, setErreurAchat] = useState<ErreurApi | null>(null);
  const [confirmation, setConfirmation] = useState<ConfirmationAchat | null>(null);
  const [achatEnCours, setAchatEnCours] = useState<string | null>(null);
  const [tentative, setTentative] = useState(0);

  /**
   * Verrou d’achat **synchrone** : l’état React est asynchrone, un double-clic dans la même
   * rafale verrait encore `achatEnCours === null`. Le verrou en `ref` rend le second clic
   * inopérant avant tout rendu.
   */
  const verrouAchat = useRef<string | null>(null);

  /**
   * Clés d’idempotence **par intention d’achat** : `upgradeId:niveauVisé`. Elles survivent aux
   * reprises réseau et ne sont retirées qu’une fois l’intention tranchée (succès ou refus).
   */
  const clesIdempotence = useRef(new Map<string, string>());

  /** Chargement en cours partagé : le mode strict de React exécute l’effet deux fois. */
  const chargementRef = useRef<{ cle: string; promesse: Promise<DonneesAmeliorations> } | null>(null);

  function chargerDonnees(cle: string): Promise<DonneesAmeliorations> {
    const existant = chargementRef.current;
    if (existant !== null && existant.cle === cle) return existant.promesse;
    const promesse = (async (): Promise<DonneesAmeliorations> => {
      const [etat, manifeste] = await Promise.all([lireAmeliorations(), chargerManifeste()]);
      return { eclats: etat.eclats, upgrades: etat.upgrades, catalogue: manifeste.ameliorations };
    })();
    chargementRef.current = { cle, promesse };
    return promesse;
  }

  useEffect(() => {
    let actif = true;
    chargerDonnees(`${tentative}`)
      .then((resultat) => {
        if (!actif) return;
        setDonnees(resultat);
        setErreurChargement(null);
      })
      .catch((erreur: unknown) => {
        if (!actif) return;
        setErreurChargement(versErreurApi(erreur));
      });
    return () => {
      actif = false;
    };
  }, [tentative]);

  const entrees: readonly EntreeAmelioration[] =
    donnees === null
      ? []
      : donnees.upgrades.map((etat) => ({
          etat,
          definition:
            donnees.catalogue.find((candidat) => candidat.id === etat.upgradeId) ?? null,
        }));

  /** Nom traduit d’une entrée (catalogue) ou identifiant brut si le contenu a changé. */
  function nomDe(entree: EntreeAmelioration): string {
    return entree.definition === null
      ? entree.etat.upgradeId
      : t(cleTraductionContenu(entree.definition.nameKey));
  }

  /**
   * Achat d’un niveau. Un seul appel réseau par intention, quel que soit le nombre de clics.
   */
  async function acheter(entree: EntreeAmelioration): Promise<void> {
    if (verrouAchat.current !== null) return;
    const { upgradeId, niveau } = entree.etat;
    verrouAchat.current = upgradeId;
    setAchatEnCours(upgradeId);
    setErreurAchat(null);
    setConfirmation(null);

    const intention = `${upgradeId}:${niveau + 1}`;
    let cle = clesIdempotence.current.get(intention);
    if (cle === undefined) {
      cle = genererCleIdempotence();
      clesIdempotence.current.set(intention, cle);
    }

    try {
      const reponse = await acheterAmelioration(upgradeId, cle);
      clesIdempotence.current.delete(intention);
      // Le solde et le niveau affichés sont **ceux du serveur** : aucun calcul local.
      setDonnees((precedent) =>
        precedent === null
          ? precedent
          : {
              eclats: reponse.eclats,
              catalogue: precedent.catalogue,
              upgrades: precedent.upgrades.map((candidat) =>
                candidat.upgradeId === reponse.upgrade.upgradeId ? reponse.upgrade : candidat,
              ),
            },
      );
      if (progression !== null) {
        definirProgression({ ...progression, eclats: reponse.eclats });
      }
      setConfirmation({
        nom: nomDe(entree),
        niveau: reponse.upgrade.niveau,
        solde: reponse.eclats,
      });
    } catch (erreur) {
      clesIdempotence.current.delete(intention);
      const refus = versErreurApi(erreur);
      setErreurAchat(refus);
      // Un refus peut signaler un état local périmé (niveau max atteint ailleurs, solde changé) :
      // on relit l’état d’autorité au lieu de corriger à la main.
      setTentative((valeur) => valeur + 1);
    } finally {
      verrouAchat.current = null;
      setAchatEnCours(null);
    }
  }

  /** Message de refus : les deux codes propres à cet écran ont un libellé dédié. */
  function messageRefus(erreur: ErreurApi): string {
    if (erreur.code === 'INSUFFICIENT_ECLATS') return t('ameliorations.refus.insuffisant');
    if (erreur.code === 'UPGRADE_MAX_LEVEL') return t('ameliorations.refus.maximum');
    if (erreur.code === 'RUN_INCOHERENT') return t('ameliorations.refus.partieEnCours');
    return t(cleI18nErreur(erreur));
  }

  return (
    <div className="ecran">
      <Entete avecDeconnexion />
      <main className="page-liste">
        <header className="page-liste__entete">
          <h1 className="page-liste__titre">{t('ameliorations.titre')}</h1>
          <p className="page-liste__sousTitre">{t('ameliorations.sousTitre')}</p>
        </header>

        {donnees === null ? (
          erreurChargement === null ? (
            <Chargement />
          ) : (
            <BandeauErreur
              erreur={erreurChargement}
              surReessai={() => {
                setTentative((valeur) => valeur + 1);
              }}
            />
          )
        ) : (
          <>
            <section className="solde" aria-label={t('ameliorations.solde')}>
              <h2 className="solde__etiquette">{t('ameliorations.solde')}</h2>
              <p className="solde__valeur" data-solde={donnees.eclats}>
                {formaterNombre(donnees.eclats, i18n.language)}
              </p>
              {/*
                Un seul emplacement d’état sous le solde : aide, confirmation d’achat ou refus.
                Ils sont mutuellement exclusifs, ce qui garde la hauteur de l’écran stable — une
                confirmation ne doit pas repousser la liste hors de l’écran.
              */}
              {erreurAchat !== null ? (
                <p className="note note--alerte" role="alert" data-refus-achat>
                  {messageRefus(erreurAchat)}
                </p>
              ) : confirmation !== null ? (
                <p className="note note--succes" role="status" data-confirmation-achat>
                  {t('ameliorations.confirmation', {
                    nom: confirmation.nom,
                    niveau: formaterNombre(confirmation.niveau, i18n.language),
                    solde: formaterNombre(confirmation.solde, i18n.language),
                  })}
                </p>
              ) : (
                <p className="note">{t('ameliorations.soldeAide')}</p>
              )}
            </section>

            <ul className="ameliorations">
              {entrees.map((entree) => {
                const { etat, definition } = entree;
                const auMaximum = etat.coutProchain === null;
                const cout = etat.coutProchain;
                const soldeInsuffisant = cout !== null && donnees.eclats < cout;
                const achetable = !auMaximum && !soldeInsuffisant;
                const nom = nomDe(entree);
                const effet = etat.effet;
                const idRaison = `raison-${etat.upgradeId}`;

                return (
                  <li className="panneau amelioration" key={etat.upgradeId} data-amelioration={etat.upgradeId}>
                    <h2 className="amelioration__nom">{nom}</h2>
                    {definition === null ? null : (
                      <p className="amelioration__description">
                        {t(cleTraductionContenu(definition.descKey))}
                      </p>
                    )}

                    <dl className="amelioration__stats">
                      <div className="amelioration__stat">
                        <dt className="amelioration__etiquette">{t('ameliorations.niveau')}</dt>
                        <dd className="amelioration__valeur" data-niveau={etat.niveau}>
                          {definition === null
                            ? formaterNombre(etat.niveau, i18n.language)
                            : t('ameliorations.niveauValeur', {
                                niveau: formaterNombre(etat.niveau, i18n.language),
                                maximum: formaterNombre(definition.maxNiveau, i18n.language),
                              })}
                        </dd>
                      </div>
                      <div className="amelioration__stat">
                        <dt className="amelioration__etiquette">{t('ameliorations.effetParNiveau')}</dt>
                        <dd className="amelioration__valeur">
                          {t(`ameliorations.effet.${effet.type}`, {
                            count: valeurEffet(effet),
                            valeur: formaterNombre(valeurEffet(effet), i18n.language),
                          })}
                        </dd>
                      </div>
                      {cout === null ? null : (
                        <div className="amelioration__stat">
                          <dt className="amelioration__etiquette">
                            {t('ameliorations.coutProchain')}
                          </dt>
                          <dd className="amelioration__valeur" data-cout={cout}>
                            {t('ameliorations.coutValeur', {
                              cout: formaterNombre(cout, i18n.language),
                            })}
                          </dd>
                        </div>
                      )}
                    </dl>

                    <Bouton
                      variante={achetable ? 'principal' : 'secondaire'}
                      disabled={!achetable}
                      enCours={achatEnCours === etat.upgradeId}
                      texteEnCours={t('ameliorations.achatEnCours')}
                      aria-describedby={achetable ? undefined : idRaison}
                      onClick={() => {
                        void acheter(entree);
                      }}
                    >
                      {cout === null
                        ? t('ameliorations.boutonMaximum')
                        : t('ameliorations.acheter', { cout: formaterNombre(cout, i18n.language) })}
                    </Bouton>

                    {auMaximum ? (
                      <p className="note note--accent" id={idRaison}>
                        {t('ameliorations.maximum')}
                      </p>
                    ) : soldeInsuffisant && cout !== null ? (
                      <p className="note note--alerte" id={idRaison}>
                        {t('ameliorations.soldeInsuffisant', {
                          manque: formaterNombre(cout - donnees.eclats, i18n.language),
                        })}
                      </p>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          </>
        )}

        <nav className="page-liste__pied" aria-label={t('commun.navigation')}>
          <Bouton variante="secondaire" onClick={() => navigate('/')}>
            {t('commun.retourAccueil')}
          </Bouton>
          <Bouton variante="discret" onClick={() => navigate('/historique')}>
            {t('ameliorations.versHistorique')}
          </Bouton>
        </nav>
      </main>
    </div>
  );
}
